'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import PlayCircle from '@/ui/illustrations/illustration/PlayCircle'
import { Picture } from '@/ui/media/picture/Picture.client'
import { Media, type Image } from '@gql-generated/graphql'
import { Button } from 'react-aria-components'

export type VideoPlayButtonProps = WithPayloadBlockId & {
  onPress?: () => void
  thumbnailUrl?: string
  alt?: string
  className?: string
  'aria-label'?: string
  iconSize?: number
}

/**
 * Centered play icon overlay
 */
export function VideoPlayIcon({ iconSize = 32 }: { iconSize?: number }): React.ReactElement {
  return (
    <PlayCircle
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full shadow-3xl"
      width={iconSize}
      height={iconSize}
    />
  )
}

export function VideoPlayButton({
  onPress,
  thumbnailUrl,
  alt = 'Video',
  className,
  'aria-label': ariaLabel,
  iconSize,
  'data-payload-block-id': dataPayloadBlockId,
}: VideoPlayButtonProps): React.ReactElement {
  const { t } = useTranslation(['videoGallery'])
  const defaultAriaLabel = t('videoGallery:button.ariaLabel')

  const image: Image = {
    alt,
    set: thumbnailUrl ? [{ url: thumbnailUrl, media: Media.Mobile }] : [],
  }

  return (
    <Button
      onPress={onPress}
      aria-label={ariaLabel || defaultAriaLabel}
      className={twMerge(
        'relative w-full cursor-pointer overflow-hidden shadow-picture',
        className,
      )}
      data-testid="video-play-button"
      data-payload-block-id={dataPayloadBlockId}
    >
      <Picture image={image} mode="cover" className="size-full" />
      <VideoPlayIcon iconSize={iconSize} />
    </Button>
  )
}
